/**
 * /solo/athlete — SAA SOLO Athlete Dashboard.
 *
 *  GAME PLAN     Today's 4 lesson cards (Math / ELA / Sci / Social Studies)
 *  TRAINING ROOM Per-course unit progress bars (all subjects, work-ahead view)
 *  STAT SHEET    Minutes today vs state minimum, streak, mastery, completed count
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2, ArrowRight, ArrowLeft, Trophy, Flame, Clock, Target, BookOpen,
  Sparkles, Lock, Check, ChevronRight,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const SUBJECT_ICON = { math: '∑', ela: '✎', science: '⚛', social_studies: '🌐' };
const SUBJECT_LABEL = { math: 'Math', ela: 'ELA', science: 'Science', social_studies: 'Social Studies' };

const AthleteDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/academy/solo/athlete/dashboard', { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load athlete dashboard');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
    </div>
  );

  if (!data?.enrollment?.active) return (
    <NotEnrolled />
  );

  const enr = data.enrollment;
  const pacing = data.pacing || {};
  const stats = data.stat_sheet || {};
  const minutesPct = stats.state_min_minutes ? Math.min(100, Math.round(100 * (stats.minutes_today || 0) / stats.state_min_minutes)) : 0;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-16 pb-24" data-testid="athlete-dashboard">
      {/* HERO */}
      <div className="border-b-2 border-zinc-900 py-8 px-6" style={{ background: 'linear-gradient(to bottom, rgba(255,87,34,0.08), transparent)' }}>
        <div className="container mx-auto max-w-6xl">
          <Link to="/solo/enroll" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-3">
            <ArrowLeft className="w-3 h-3" /> Enrollment
          </Link>
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500">GAME PLAN · TODAY</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
              Day {pacing.day_N + 1 || 1} of {pacing.total_days || 180} · Grade {enr.grade_level} · {enr.state_of_record}
            </div>
          </div>
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.02] mt-2">
            Suit Up, <span className="text-orange-500">Scholar Athlete</span>
          </h1>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-6 py-8 space-y-10">
        {/* GAME PLAN — 4 cards */}
        <section>
          <SectionTitle>Game Plan</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(data.game_plan || []).map((gp) => (
              <LessonCard key={gp.course.id} gp={gp}
                onClick={() => navigate(`/solo/lesson/${encodeURIComponent(gp.lesson.id)}`)} />
            ))}
          </div>
        </section>

        {/* STAT SHEET strip */}
        <section>
          <SectionTitle>Stat Sheet</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBig icon={<Clock className="w-4 h-4" />} label="Minutes Today" value={`${stats.minutes_today || 0}`}
              sub={`/ ${stats.state_min_minutes || 240} min`} pct={minutesPct} testid="stat-minutes" />
            <StatBig icon={<Flame className="w-4 h-4" />} label="Streak" value={`${stats.streak_days || 0}`}
              sub={stats.streak_days === 1 ? 'day' : 'days'} testid="stat-streak" />
            <StatBig icon={<Target className="w-4 h-4" />} label="Mastery" value={stats.mastery_pct != null ? `${stats.mastery_pct}%` : '—'}
              sub="avg score" testid="stat-mastery" />
            <StatBig icon={<Trophy className="w-4 h-4" />} label="Completed" value={`${stats.lessons_completed || 0}`}
              sub="lessons" testid="stat-completed" />
          </div>
          {stats.present_today ? (
            <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-orange-500 font-black flex items-center gap-1.5">
              <Check className="w-3 h-3" /> Marked present for {enr.state_of_record}
            </div>
          ) : (
            <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-black">
              Need {Math.max(0, (stats.state_min_minutes || 240) - (stats.minutes_today || 0)).toFixed(0)} more minutes for today's attendance
            </div>
          )}
        </section>

        {/* TRAINING ROOM */}
        <section>
          <SectionTitle>Training Room · All Subjects</SectionTitle>
          <div className="border-2 border-zinc-900 divide-y-2 divide-zinc-900">
            {(data.training_room || []).map((t) => (
              <TrainingRow key={t.course_id} t={t}
                onClick={() => navigate(`/solo/course/${encodeURIComponent(t.course_id)}`)} />
            ))}
          </div>
        </section>

        {/* PACING footer */}
        <section className="border-2 border-orange-500/40 bg-orange-500/5 p-5">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500">Pacing</div>
          <div className="text-2xl font-black mt-1">Day {pacing.day_N + 1 || 1} · {pacing.percent_complete || 0}% through the year</div>
          <div className="w-full h-2 bg-black mt-3">
            <div className="h-full bg-orange-500 transition-all" style={{ width: `${pacing.percent_complete || 0}%` }} />
          </div>
          <p className="text-[11px] text-zinc-400 mt-3">Max unlocked lesson index: {pacing.max_unlocked_lesson + 1 || 1}. Cap: 1 week ahead — Coach B says work today's plan first.</p>
        </section>
      </div>
    </div>
  );
};

// ─── Sub-components ─────────────────────────────────────────────────────────
const NotEnrolled = () => (
  <div className="min-h-screen bg-[#0B0E14] text-white pt-24 pb-24 flex items-center justify-center">
    <div className="text-center max-w-md px-6" data-testid="athlete-not-enrolled">
      <Lock className="w-10 h-10 mx-auto text-orange-500 mb-4" />
      <h1 className="text-3xl font-black uppercase tracking-tight mb-3">Suit Up First</h1>
      <p className="text-sm text-zinc-400 mb-6">You need an active SOLO enrollment to see the Athlete Dashboard.</p>
      <Link to="/solo/enroll" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-black px-5 py-3 text-[11px] font-black uppercase tracking-[0.2em]" data-testid="go-enroll">
        Enroll in SOLO <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-3">{children}</div>
);

const LessonCard = ({ gp, onClick }) => {
  const done = gp.lesson.status === 'completed';
  return (
    <button onClick={onClick}
      className={`text-left p-4 border-2 transition-all group ${done ? 'border-orange-500/40 bg-orange-500/5' : 'border-zinc-800 hover:border-orange-500 bg-zinc-950'}`}
      data-testid={`game-plan-card-${gp.course.subject}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-2xl font-black text-orange-500">{SUBJECT_ICON[gp.course.subject] || '•'}</div>
        {done ? (
          <div className="text-[9px] font-black uppercase tracking-widest text-orange-500 flex items-center gap-1"><Check className="w-3 h-3" /> Done</div>
        ) : (
          <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500">{gp.lesson.minutes_est || 20}m</div>
        )}
      </div>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">{SUBJECT_LABEL[gp.course.subject] || gp.course.subject}</div>
      <div className="text-sm font-black leading-tight text-white line-clamp-2">{gp.lesson.title}</div>
      <div className="text-[10px] text-zinc-500 mt-2 line-clamp-1">{gp.lesson.unit_title}</div>
      <div className="mt-3 flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-orange-500 group-hover:translate-x-0.5 transition-transform">
        Open <ChevronRight className="w-3 h-3" />
      </div>
    </button>
  );
};

const StatBig = ({ icon, label, value, sub, pct, testid }) => (
  <div className="border-2 border-zinc-900 bg-zinc-950 p-4" data-testid={testid}>
    <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-black flex items-center gap-1.5">{icon} {label}</div>
    <div className="text-3xl font-black text-white mt-1.5">{value}</div>
    <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>
    {pct != null && (
      <div className="w-full h-1 bg-black mt-3">
        <div className="h-full bg-orange-500" style={{ width: `${pct}%` }} />
      </div>
    )}
  </div>
);

const TrainingRow = ({ t, onClick }) => {
  const pct = t.unit_count ? Math.round(100 * t.current_unit / t.unit_count) : 0;
  return (
    <button onClick={onClick} className="w-full flex items-center gap-4 px-4 py-3 hover:bg-zinc-900/40 text-left" data-testid={`training-row-${t.subject}`}>
      <div className="text-2xl font-black text-orange-500 w-8">{SUBJECT_ICON[t.subject] || '•'}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-black text-white truncate">{t.title}</div>
        <div className="w-full h-1.5 bg-black mt-2">
          <div className="h-full bg-orange-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="text-right">
        <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Unit</div>
        <div className="text-lg font-black text-white leading-none">{t.current_unit}<span className="text-xs text-zinc-500">/{t.unit_count}</span></div>
      </div>
      <ChevronRight className="w-4 h-4 text-zinc-600" />
    </button>
  );
};

export default AthleteDashboard;
